/*
 * The site search palette.
 *
 * Opens on Ctrl+K or Cmd+K, or from the magnifier in the header. Everything here is
 * presentation and keyboard handling: the searching itself is the Livewire component the
 * modal wraps, so results stay permission filtered on the server.
 *
 * Keyboard navigation walks the rendered rows rather than a copy of the results held in
 * JavaScript. The rows are re-rendered by Livewire on every keystroke, so anything cached
 * here would be describing the previous set of results by the time somebody pressed
 * enter.
 */

/* How often to check whether the closing panel has finished its transition. */
const CLOSE_POLL = 25;

document.addEventListener('alpine:init', () => {

	Alpine.data('globalSearchPalette', () => {

		/*
		 * The palette element and the observer are held in the closure rather than on the
		 * returned object. Anything put on that object becomes part of Alpine's reactive
		 * data and is wrapped in a proxy, and calling a native method through such a proxy
		 * fails with "Illegal invocation" — which is what observer.disconnect() did when a
		 * page navigation tore the component down.
		 *
		 * The factory runs once per component, so these stay per instance.
		 */
		let root = null;
		let observer = null;

		return {

			/*
			 * Named isOpen rather than open on purpose. Alpine resolves an expression like
			 * x-show="open" by walking the scope chain and falling through to the window
			 * when nothing matches, and window.open is a native function. While the
			 * component is being torn down for a page navigation the data is no longer
			 * there to match, the lookup reaches window.open, and Alpine calls it with the
			 * scope proxy as its receiver, which throws "Illegal invocation".
			 */
			isOpen: false,
			active: 0,

			init() {

				/*
				 * $el is whichever element the expression being evaluated sits on, so
				 * inside the input's own key handlers it is the input, not the palette.
				 * The root is captured here, where $el is the palette, and every lookup
				 * goes through it. Without this, looking for the result rows from a key
				 * handler searches inside the input and finds none.
				 */
				root = this.$el;

				/*
				 * Livewire swaps the result rows underneath us. Whenever they change, the
				 * highlight is put back within range, since the row that was highlighted
				 * may no longer exist.
				 */
				observer = new MutationObserver(() => this.clampActive());
				observer.observe(this.$refs.results ?? root, { childList: true, subtree: true });

				/*
				 * Nothing reactive is written here on purpose. Doing so would re-run
				 * x-show against a scope Alpine has already begun disposing, throwing once
				 * per binding. The palette needs no closing anyway: a navigation
				 * re-renders it from scratch, shut.
				 */
			},

			destroy() {
				observer?.disconnect();
				observer = null;
			},

			toggle() {
				this.isOpen ? this.close() : this.show();
			},

			show() {

				this.isOpen = true;
				this.active = 0;

				/* The input is only focusable once the panel is actually shown. */
				this.$nextTick(() => this.$refs.input?.focus());
			},

			close() {

				this.isOpen = false;
				this.active = 0;

				this.clearOnceHidden();
			},

			/*
			 * Leaving the term behind would show stale results on the next open, but
			 * clearing it straight away empties the result list while the panel is still
			 * fading out, so the rows visibly blank a moment before it goes.
			 *
			 * Waiting a fixed delay would only be guessing at a duration set in the
			 * markup, so this waits for the overlay to actually be hidden. Gives up after
			 * a second in case the transition never ends, and does nothing if they
			 * reopened meanwhile, which would otherwise wipe out what they just typed.
			 */
			clearOnceHidden(attempts = 0) {

				if (this.isOpen) {
					return;
				}

				const overlay = root?.firstElementChild;
				const hidden = ! overlay || getComputedStyle(overlay).display === 'none';

				if (! hidden && attempts < 40) {
					setTimeout(() => this.clearOnceHidden(attempts + 1), CLOSE_POLL);

					return;
				}

				this.$wire && this.$wire.clear();
			},

			rows() {
				return Array.from(root?.querySelectorAll('[data-search-result]') ?? []);
			},

			clampActive() {

				const count = this.rows().length;

				if (count === 0) {
					this.active = 0;

					return;
				}

				if (this.active > count - 1) {
					this.active = count - 1;
				}
			},

			move(delta) {

				const rows = this.rows();

				if (rows.length === 0) {
					return;
				}

				/* Wraps, so holding the down arrow at the end goes back to the top. */
				this.active = (this.active + delta + rows.length) % rows.length;

				rows[this.active]?.scrollIntoView({ block: 'nearest' });
			},

			/*
			 * Enter follows whatever is highlighted. With nothing to highlight it falls
			 * through to the full results page, which is what somebody who types and hits
			 * enter without looking expects.
			 */
			choose() {

				const rows = this.rows();

				if (rows.length === 0) {
					this.viewAll();

					return;
				}

				rows[this.active]?.click();
			},

			viewAll() {

				const link = this.$refs.viewAll;

				if (link) {
					link.click();
				}
			},

			isActive(index) {
				return this.active === index;
			},

		};

	});

});
